import React, { useState } from "react";
import { AlertOctagon, RefreshCw, Plus, ArrowRight, Zap, Package, TrendingDown } from "lucide-react";
import { useApp } from "../../app/context/AppContext";
import { DataTable } from "../../components/common/DataTable";
import { Badge } from "../../components/common/Badge";
import { formatCurrency } from "../../utils/formatters";
import { useNavigate } from "react-router-dom";
import { PurchaseOrderModal } from "../procurement/PurchaseOrderModal";

export function DeadStockModule() {
  const { products, batches, salesOrders, metrics } = useApp();
  const [activeTab, setActiveTab] = useState("reorder"); // 'reorder' | 'deadstock'
  const [isPOModalOpen, setIsPOModalOpen] = useState(false);
  const navigate = useNavigate();

  // Reorder suggestion data
  const reorderList = products
    .filter((p) => {
      const stock = metrics.productStockMap[p.id] || 0;
      return stock <= p.reorderLevel;
    })
    .map((p) => {
      const currentStock = metrics.productStockMap[p.id] || 0;
      const suggestedQty = p.reorderLevel * 3 - currentStock;
      const estimatedCost = suggestedQty * p.purchasePrice;
      return {
        ...p,
        currentStock,
        suggestedQty,
        estimatedCost
      };
    });

  // Dead stock data (unsold for 45+ days with locked capital)
  const deadStockList = metrics.deadStockProducts.map((p) => {
    const stock = metrics.productStockMap[p.id] || 0;
    const lockedValue = stock * p.purchasePrice;
    return {
      ...p,
      currentStock: stock,
      daysUnsold: 62,
      lockedValue,
      suggestedAction: stock > 30 ? "Return to Supplier Credit" : "Apply 25% Clearance Sale"
    };
  });

  const reorderColumns = [
    {
      header: "Product SKU",
      accessorKey: "name",
      render: (val, row) => (
        <div>
          <span className="font-bold text-slate-900 dark:text-white">{val}</span>
          <p className="text-[10px] text-slate-400 font-mono">Barcode: {row.barcode}</p>
        </div>
      )
    },
    {
      header: "Current Stock",
      accessorKey: "currentStock",
      align: "center",
      render: (val, row) => (
        <span className="font-mono font-bold text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/60 px-2 py-0.5 rounded-lg">
          {val} {row.unit}s
        </span>
      )
    },
    {
      header: "Reorder Threshold",
      accessorKey: "reorderLevel",
      align: "center",
      render: (val) => <span className="font-mono text-slate-500 font-semibold">{val} units</span>
    },
    {
      header: "Suggested PO Qty",
      accessorKey: "suggestedQty",
      align: "center",
      render: (val, row) => (
        <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">
          +{val} {row.unit}s
        </span>
      )
    },
    {
      header: "Est. Capital (₹)",
      accessorKey: "estimatedCost",
      align: "right",
      render: (val) => (
        <span className="font-mono font-bold text-slate-900 dark:text-white">
          {formatCurrency(val)}
        </span>
      )
    },
    {
      header: "1-Click Action",
      accessorKey: "actions",
      sortable: false,
      render: () => (
        <button
          onClick={() => setIsPOModalOpen(true)}
          className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-semibold text-[10px] shadow"
        >
          <Zap className="w-3 h-3" />
          Create PO
        </button>
      )
    }
  ];

  const deadStockColumns = [
    {
      header: "Slow SKU",
      accessorKey: "name",
      render: (val, row) => (
        <div>
          <span className="font-bold text-slate-900 dark:text-white">{val}</span>
          <p className="text-[10px] text-slate-400">{row.category}</p>
        </div>
      )
    },
    {
      header: "Locked Stock",
      accessorKey: "currentStock",
      align: "center",
      render: (val, row) => <span className="font-mono font-bold text-slate-700 dark:text-slate-300">{val} {row.unit}s</span>
    },
    {
      header: "Days Unsold",
      accessorKey: "daysUnsold",
      align: "center",
      render: (val) => <Badge variant="rose">{val} days idle</Badge>
    },
    {
      header: "Locked Capital (₹)",
      accessorKey: "lockedValue",
      align: "right",
      render: (val) => (
        <span className="font-mono font-bold text-rose-600 dark:text-rose-400">
          {formatCurrency(val)}
        </span>
      )
    },
    {
      header: "Suggested Recovery Action",
      accessorKey: "suggestedAction",
      render: (val) => <span className="text-[11px] font-semibold text-amber-600 dark:text-amber-400">{val}</span>
    }
  ];

  return (
    <div className="space-y-6">
      {/* Top Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display flex items-center gap-2">
            <AlertOctagon className="w-5 h-5 text-emerald-500" />
            Dead Stock Recovery & Intelligent Reorder Engine
          </h2>
          <p className="text-xs text-slate-500">
            Identify locked working capital in idle stock & generate instant purchase orders for low-stock items
          </p>
        </div>

        <div className="flex p-1 bg-slate-100 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 text-xs">
          <button
            onClick={() => setActiveTab("reorder")}
            className={`px-3.5 py-1.5 rounded-lg font-semibold transition ${
              activeTab === "reorder" ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-xs" : "text-slate-500 hover:text-slate-800"
            }`}
          >
            Reorder Suggestions ({reorderList.length})
          </button>
          <button
            onClick={() => setActiveTab("deadstock")}
            className={`px-3.5 py-1.5 rounded-lg font-semibold transition ${
              activeTab === "deadstock" ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-xs" : "text-slate-500 hover:text-slate-800"
            }`}
          >
            Dead Stock & Idle Capital ({deadStockList.length})
          </button>
        </div>
      </div>

      {activeTab === "reorder" ? (
        <DataTable
          columns={reorderColumns}
          data={reorderList}
          title="Auto-Computed Reorder Recommendations"
          subtitle="Products below minimum threshold with 1-click PO creation"
          filename="kirana_reorder_recommendations"
        />
      ) : (
        <DataTable
          columns={deadStockColumns}
          data={deadStockList}
          title="Dead Stock & Slow Movement Analysis"
          subtitle="Capital unlocking recommendations (discounting vs supplier returns)"
          filename="kirana_dead_stock_recovery"
        />
      )}

      <PurchaseOrderModal
        isOpen={isPOModalOpen}
        onClose={() => setIsPOModalOpen(false)}
      />
    </div>
  );
}
